const parseStoredJSON = (key) => {
  const raw = localStorage.getItem(key)
  if (!raw || raw === 'undefined' || raw === 'null') return undefined

  try {
    return JSON.parse(raw)
  } catch (err) {
    console.warn(`AuthService: invalid ${key} in localStorage, clearing it`)
    localStorage.removeItem(key)
    return undefined
  }
}

const setStoredJSON = (key, value) => {
  if (value === undefined || value === null) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, JSON.stringify(value))
}

const setUser = (userObj) => {
  setStoredJSON('user', userObj)
}

const setProfile = (profileObj) => {
  setStoredJSON('profile', profileObj)
}

const setPermissions = (permissionsObj) => {
  setStoredJSON('permissions', permissionsObj)
}

const getPermissions = () => {
  return parseStoredJSON('permissions')
}

const isAccountOwner = () => {
  return getPermissions()?.name === 'Account Owner'
}

const isAdmin = () => {
  return getPermissions()?.name === 'Admin'
}

const canEditEmailTemplates = () => {
  return isAccountOwner() || isAdmin()
}

const canManageTeam = () => {
  return isAccountOwner() || isAdmin()
}

const ACTIVE_ORG_UUID_KEY = 'activeOrganizationUuid'
const SUPER_ADMIN_ORGANIZER_KEY = 'isSuperAdminOrganizer'

const setSuperAdminOrganizerSession = (isSuperAdminOrganizer) => {
  if (isSuperAdminOrganizer) {
    localStorage.setItem(SUPER_ADMIN_ORGANIZER_KEY, 'true')
  } else {
    localStorage.removeItem(SUPER_ADMIN_ORGANIZER_KEY)
  }
}

const isSuperAdminOrganizer = () => {
  if (localStorage.getItem(SUPER_ADMIN_ORGANIZER_KEY) === 'true') {
    return true
  }

  const user = getUser()?.user
  return Boolean(user?.superAdmin && user?.role?.name === 'Organizer')
}

const setOrg = (orgObj) => {
  if (Array.isArray(orgObj)) {
    orgObj.forEach((o) => {
      if (o && o.events !== undefined) delete o.events
    })
  }
  let org = JSON.stringify(orgObj)
  localStorage.setItem('org', org)
}

const reorderOrganizationsWithActive = (orgs, activeUuid) => {
  if (!Array.isArray(orgs) || orgs.length === 0) return orgs
  const uuid =
    activeUuid && orgs.some((o) => o?.uuid === activeUuid)
      ? activeUuid
      : orgs[0]?.uuid
  const idx = orgs.findIndex((o) => o?.uuid === uuid)
  if (idx <= 0) return orgs
  const next = [...orgs]
  const [chosen] = next.splice(idx, 1)
  return [chosen, ...next]
}

const applyOrganizationSession = (organizations, activeUuid) => {
  const reordered = reorderOrganizationsWithActive(organizations, activeUuid)
  setOrg(reordered)
  const primary = reordered[0]?.uuid
  if (primary) {
    localStorage.setItem(ACTIVE_ORG_UUID_KEY, primary)
  }
}

const switchActiveOrganization = (uuid) => {
  applyOrganizationSession(getOrg() || [], uuid)
}

const getUser = () => {
  return parseStoredJSON('user')
}

const setSignUpToken = (jwt) => {
  let token = JSON.stringify(jwt)
  localStorage.setItem('signup-token', token)
}

const getSignUpToken = () => {
  return parseStoredJSON('signup-token')
}

const getOrg = () => {
  return parseStoredJSON('org')
}

const getProfile = () => {
  return parseStoredJSON('profile')
}

const token = () => {
  const user = getUser()
  if (user) {
    return user.jwt
  } else {
    return undefined
  }
}

const isLoggedIn = () => {
  const user = getUser()
  if (!user) return false
  return Object.keys(user).length !== 0
}

const logoutUser = () => {
  localStorage.removeItem('user')
  localStorage.removeItem('org')
  localStorage.removeItem('permissions')
  localStorage.removeItem('profile')
  localStorage.removeItem('pos-posdeviceid')
  localStorage.removeItem(ACTIVE_ORG_UUID_KEY)
  localStorage.removeItem(SUPER_ADMIN_ORGANIZER_KEY)
}

const removeSignup = () => {
  localStorage.removeItem('signup-token')
}

const isOrganizer = () => {
  const jwt = getUser()
  if (!jwt) return false
  return jwt.user.role.name === 'Organizer'
}

const atts = {
  setUser,
  setSignUpToken,
  setOrg,
  applyOrganizationSession,
  switchActiveOrganization,
  setSuperAdminOrganizerSession,
  isSuperAdminOrganizer,
  setProfile,
  getUser,
  getSignUpToken,
  getOrg,
  getProfile,
  isLoggedIn,
  removeSignup,
  logoutUser,
  token,
  isOrganizer,
  setPermissions,
  getPermissions,
  isAccountOwner,
  isAdmin,
  canEditEmailTemplates,
  canManageTeam,
}

export default atts
